/*
   USER HINTS:
   these replace the "notification" messages we had before, which displayed in a black box below the map

   ASSUMPTIONS
   - each hint only appears if certain conditions are true
      - the hint disappears if those conditions cease to be true, but this won't *dismiss* it (it appears again if those conditions again become true)
   - each hint can be dismissed by clicking its X, OR by certain user actions (which suggest the hint has been understood and is no longer needed)
      - once a hint is dismissed, it will never appear again (even after page refresh)
   - only one hint can appear at a time
   - hints do not display on mobile

   SPECIFICATIONS
   each user hint is deined by its:
   - text (content of the hint)
   - location (where it appears on the page, default is bottom center)
   - conditions (the state the app must be in for it to display, assuming it hasn't been dismissed)
   - dismissal (the action(s) that dismiss it, aside from clicking its X)
   - id (arbitrary string for tracking)

   HINTS
   id: norming
   text: "use Level of comparison menu to change norming mode..."
   location: points at "data controls" icon on side panel
   conditions: slideout panel is closed, national norming mode is active (so automatically appears when app loads with normal settings)
   dismissal: opening the data controls panel

   id: activateMetro/State // 2 separate hints, but which behave analogously
   text: "click metro/state to view its data"
   location: default (below map) // where the old notifications appeared
   conditions: metro/state norming mode is active
   dismissal: user clicks on a metro/state

   id: exploreRedlining
   text: "Use these checkboxes to explore how Home Owner Loan Corporation (HOLC) agents’ classified city neighborhoods..."
   location: points at redlining overlay toggle
   conditions: redlining active, overlays panel open
   dismissal: // only be dismissed with its X, as there is important contextual info that we can't know a user has absorbed simply based on them clicking a checkbox
  */

import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import i18n from '@pureartisan/simple-i18n'
import shallow from 'zustand/shallow'
import { MdClose } from 'react-icons/md'
import { IconButton, Typography } from '@material-ui/core'

import useStore from '../store'

const fadeDuration = 800
const useStyles = makeStyles(theme => ({
  root: {
    ...theme.extras.UserHints,
    // don't show on xs screens
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
    backgroundColor: '#1071E0',
    borderRadius: '5px',
    position: 'absolute',
    bottom: '36px',
    padding: '18px 36px 18px 18px',
    fontFamily: 'Fira Sans',
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 400,
    // center notification
    right: `calc(50% - (${theme.extras.UserHints.maxWidth}px / 2))`,
    margin: 0,
    '& .fake-link': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    '& p': {
      color: theme.extras.variables.colors.white,
    },
    '&:not(.isPositioned) $arrow': {
      display: 'none',
    },
    opacity: 0,
    transition: theme.transitions.create(['opacity'], {
      duration: fadeDuration,
    }),
    '&.isActive': {
      opacity: 1,
    },
  },
  arrow: {
    width: '0px',
    height: '0px',
    borderTop: '10px solid transparent',
    borderBottom: '10px solid transparent',
    borderRight: '10px solid #1071E0',
    position: 'absolute',
    top: 'calc(50% - 10px)',
    left: '-10px',
  },
  button: {
    position: 'absolute',
    right: 0,
    top: 0,
    '& svg': {
      width: '20px',
      height: '20px',
      color: 'white',
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  text: {},
}))

const UserHints = () => {
  const {
    activeNorm,
    activeRegion,
    activeGeoType,
    slideoutPanel,
    redliningActive,
    showDensityOverlays,
    activeUserHint,
    setStoreValues,
  } = useStore(
    state => ({
      activeNorm: state.activeNorm,
      activeRegion: state.activeRegion,
      activeGeoType: state.activeGeoType,
      slideoutPanel: state.slideoutPanel,
      redliningActive: state.redliningActive,
      showDensityOverlays: state.showDensityOverlays,
      activeUserHint: state.activeUserHint,
      setStoreValues: state.setStoreValues,
    }),
    shallow,
  )
  const {
    active: slideoutPanelActive,
    panel: slideoutPanelPanel,
  } = slideoutPanel
  // used to apply class to fade hint before it's removed
  const [isActive, setIsActive] = useState(
    !!activeUserHint.id,
  )
  // HACK: unused piece of state that we use to trigger a rerender
  // in order to adjust positioned user hint location
  const [isDOA, setIsDensityOverlaysActive] = useState(
    !!showDensityOverlays,
  )

  // since we display the first hint whose conditions are met, the order indicates prioritization
  const hints = [
    {
      id: 'norming',
      location: {
        targetsElement: true,
      },
      meetsConditions: () => {
        return !slideoutPanelActive && activeNorm === 'n'
      },
    },
    {
      id: 'exploreRedlining',
      location: {
        targetsElement: true,
        leftPadding: 20,
      },
      style: {
        maxWidth: 420,
      },
      meetsConditions: () => {
        return (
          activeGeoType === 'tracts' &&
          !!redliningActive &&
          !!slideoutPanelActive &&
          slideoutPanelPanel === 'overlays'
        )
      },
    },
    {
      id: 'activateState',
      meetsConditions: () => {
        return activeNorm === 's'
      },
    },
    {
      id: 'activateMetro',
      meetsConditions: () => {
        return activeNorm === 'm'
      },
    },
  ]

  const isDismissed = hintId =>
    String(
      localStorage.getItem(`${hintId}_hint_dismissed`),
    ) === 'true'

  // record that a hint has been dismissed for future sessions
  const dismissHint = hintId => {
    if (isDismissed(hintId)) return

    localStorage.setItem(`${hintId}_hint_dismissed`, true)
    if (activeUserHint.id === hintId) {
      // hint was also active, so fade and then remove hint
      setIsActive(false)
      setTimeout(() => {
        setStoreValues({ activeUserHint: {} })
      }, fadeDuration)
    }
  }

  // TODO: use portals instead?
  const getPositionedHintLocation = hint => {
    // if a hint targetsElement, we look for an onscreen element with the
    // following class to determine where to position the hint
    const targetClass = '.user-hints-target-' + hint.id
    const target = document.querySelector(targetClass)
    if (!target) {
      // console.log(
      //   hint.id,
      //   ' user hint target not found on screen.',
      // )
      return null
    }
    return target.getBoundingClientRect()
  }

  const activateHint = hint => {
    // console.log(`${hint.id} activating`)
    if (hint.location?.targetsElement) {
      // calculating rect now allows fade of hint in place even after target has
      // left screen. (it also reduces # of times rect is calculated.)
      const targetRect = getPositionedHintLocation(hint)
      if (!targetRect) return
      hint.targetRect = targetRect
    }

    // console.log(`activating ${hint.id} hint`)
    setStoreValues({ activeUserHint: hint })
    // now re-add active class to fade in
    setTimeout(() => setIsActive(true), fadeDuration)
  }

  // auto-dismiss norming hint when control panel opened
  useEffect(() => {
    const hintId = 'norming'
    const controlPanelOpen =
      !!slideoutPanelActive &&
      slideoutPanelPanel === 'control'
    if (controlPanelOpen) dismissHint(hintId)
  }, [slideoutPanelPanel, slideoutPanelActive, activeNorm])

  // auto-dismiss activateState && activateMetro hints when region activated
  useEffect(() => {
    const hintId = {
      s: 'activateState',
      m: 'activateMetro',
    }[activeNorm]
    if (!!activeRegion && !!hintId) dismissHint(hintId)
  }, [activeRegion, activeNorm])

  // activate the first relevant, undismissed hint found when various settings change
  useEffect(() => {
    const someHintActive = hints.some(hint => {
      if (!isDismissed(hint.id) && hint.meetsConditions()) {
        // we've found a hint that hasn't been dismissed and is relevant to current conditions
        if (!activeUserHint.id) {
          // no hint is currently active, activate found hint
          // console.log(`${hint.id} will activate`)
          setTimeout(() => activateHint(hint), fadeDuration)
        } else if (activeUserHint.id !== hint.id) {
          // there's an active hint that must be replaced
          // console.log(
          //   `set hint inactive to hide ${activeUserHint.id} before ${hint.id} activated`,
          // )
          // we're about to activate a hint - set current hint inactive to fade
          setIsActive(false)
          setTimeout(() => activateHint(hint), fadeDuration)
        } else if (hint.location?.target) {
          // it's already active (showDensityOverlays value must have changed)
          // console.log(`${hint.id} hint still active.`)
          /*
           * HACK to keep redlining hint aligned with shifted toggle position:
           * recalculate targetRect and attach to hint w/o triggering
           */
          activeUserHint.targetRect = getPositionedHintLocation(
            hint,
          )
          // ...then trigger rerender, but one which won't trigger this useEffect
          setIsDensityOverlaysActive(!!showDensityOverlays)
        }
        // returning true short-circuits .some so we don't continue checking the hints
        return true
      }
    })

    // if none found, clear active hint
    if (!someHintActive && !!activeUserHint.id) {
      // console.log(
      //   `clearing ${activeUserHint.id} hint, none active`,
      // )
      setIsActive(false)
      setTimeout(() => {
        setStoreValues({ activeUserHint: {} })
      }, fadeDuration)
    }
  }, [
    activeNorm,
    slideoutPanelActive,
    slideoutPanelPanel,
    redliningActive,
    showDensityOverlays,
    activeUserHint,
  ])

  const content =
    activeUserHint.id &&
    i18n.translate(
      `USER_HINT_${activeUserHint.id.toUpperCase()}`,
      {
        geoType: i18n.translate(
          `GEO_TYPE_SIMPLE_${activeGeoType.toUpperCase()}`,
        ),
      },
    )

  const classes = useStyles()

  const handleClose = () => {
    dismissHint(activeUserHint.id)
  }

  if (!activeUserHint.id) return null

  let style = {}
  let isPositioned = false

  // position hints that point to specific app elements
  if (activeUserHint.targetRect) {
    isPositioned = true
    style.left = activeUserHint.targetRect.right
    style.top =
      (activeUserHint.targetRect.top +
        activeUserHint.targetRect.bottom) /
      2
    style.bottom = 'unset'
    let transformHorizontal =
      activeUserHint.location.leftPadding || 10
    // pad horizontally and align vertically
    style.transform = `translate(${transformHorizontal}px, -50%)`
    // so that on small screens the hint will squish rather than run off
    style.right = transformHorizontal + 3
  }

  if (activeUserHint.style) {
    style = {
      ...style,
      ...activeUserHint.style,
    }
  }

  // certain hints contain triggers to open a faq
  const handleFakeLinkClick = e => {
    if (
      e.target.classList.contains('fake-link') &&
      !!e.target.dataset.faq
    ) {
      const faqId = e.target.dataset.faq
      setStoreValues({
        slideoutPanel: {
          active: true,
          panel: 'faq',
        },
        expandedFaqs: { [faqId]: true },
      })

      setTimeout(() => {
        // scroll to question, whether in modal or slideout panel
        const elem = document.querySelector(`#faq-${faqId}`)
        elem && elem.scrollIntoView({ behavior: 'smooth' })
      })
    }
  }

  // console.log({ isActive }, activeUserHint.id)
  return (
    <div
      style={style}
      className={clsx(classes.root, 'user-hint', {
        isPositioned,
        isActive,
      })}
    >
      <div className={classes.arrow}></div>
      <IconButton
        aria-label="close"
        className={clsx(classes.button)}
        onClick={handleClose}
      >
        <MdClose />
      </IconButton>
      <Typography
        onClick={handleFakeLinkClick}
        dangerouslySetInnerHTML={{
          __html: content,
        }}
        className={clsx(classes.text)}
      />
    </div>
  )
}

export default UserHints
